import { useCallback, useRef } from "react";
import { useGameStore } from "../store/useGameStore";
import { FormattedNumber } from "./FormattedNumber";
import { HeroAvatarHead } from "./HeroAvatarHead";
import { compactNumberTitle, formatExactNumber } from "../utils/formatNumber";

const ADMIN_TAP_WINDOW_MS = 700;
const ADMIN_TAP_COUNT = 3;

export function PlayerHeaderBar() {
  const { balance, hero, user, collectIdle, setScreen } = useGameStore();
  const adminTapRef = useRef<{ count: number; timer: ReturnType<typeof setTimeout> | null }>({
    count: 0,
    timer: null,
  });

  const registerAdminGoldTap = useCallback(() => {
    if (!user?.is_admin) return;

    adminTapRef.current.count += 1;
    if (adminTapRef.current.timer) clearTimeout(adminTapRef.current.timer);

    if (adminTapRef.current.count >= ADMIN_TAP_COUNT) {
      adminTapRef.current.count = 0;
      setScreen("admin");
      return;
    }

    adminTapRef.current.timer = setTimeout(() => {
      adminTapRef.current.count = 0;
      adminTapRef.current.timer = null;
    }, ADMIN_TAP_WINDOW_MS);
  }, [setScreen, user?.is_admin]);

  if (!balance) return null;

  return (
    <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gradient-to-b from-kingdom-purple to-[#1a1228] border-b border-kingdom-mid/80 shrink-0 safe-top">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {hero && (
          <>
            <HeroAvatarHead
              heroClass={hero.hero_class}
              gender={hero.gender}
              level={hero.level}
              onClick={() => setScreen("hero")}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-bold text-white truncate">{hero.name}</span>
                <span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_6px_rgba(52,211,153,0.8)]"
                  aria-hidden
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
        <ResourcePill
          icon="🪙"
          value={balance.gold}
          interactive
          extra={balance.idle_gold_pending > 0 ? balance.idle_gold_pending : undefined}
          onCollect={balance.idle_gold_pending > 0 ? collectIdle : undefined}
          onAreaTap={user?.is_admin ? registerAdminGoldTap : undefined}
        />
        <ResourcePill icon="💎" value={balance.diamonds} interactive />
        {hero && (
          <ResourcePill icon="⚡" valueText={`${hero.energy}/${hero.max_energy}`} tone="energy" />
        )}
      </div>
    </div>
  );
}

interface PillProps {
  icon: string;
  value?: number;
  valueText?: string;
  extra?: number;
  onCollect?: () => void;
  onAreaTap?: () => void;
  tone?: "gold" | "energy";
  interactive?: boolean;
}

function ResourcePill({
  icon,
  value,
  valueText,
  extra,
  onCollect,
  onAreaTap,
  tone = "gold",
  interactive,
}: PillProps) {
  const valueClass = tone === "energy" ? "text-sky-300" : "text-gold-400";

  return (
    <div className="flex items-center gap-1">
      <div
        className="flex items-center gap-1 active:opacity-80"
        onClick={onAreaTap}
        onKeyDown={
          onAreaTap
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onAreaTap();
                }
              }
            : undefined
        }
        role={onAreaTap ? "button" : undefined}
        tabIndex={onAreaTap ? 0 : undefined}
      >
        <span className="text-sm leading-none select-none">{icon}</span>
        <div className="flex flex-col leading-none">
          <span className={`${valueClass} font-bold text-xs sm:text-sm tabular-nums`}>
            {valueText ?? (value != null ? <FormattedNumber value={value} interactive={interactive} /> : null)}
          </span>
        </div>
      </div>
      {extra != null && extra > 0 && onCollect && (
        <button
          type="button"
          onClick={onCollect}
          title={compactNumberTitle(extra) ? `+${formatExactNumber(extra)}` : undefined}
          className="text-emerald-400 text-[10px] hover:text-emerald-300 transition-colors whitespace-nowrap"
        >
          +<FormattedNumber value={extra} interactive />
        </button>
      )}
    </div>
  );
}
